import React, { useState, useRef, useEffect } from "react";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { InquiryService } from "../../../service/admin/inquiryService";
import { formatDate } from "../../../helper/utils";

/**
 * Reusable notes modal for both PDP Inquiries and Contact Us.
 *
 * Props:
 *  - open          : boolean — whether the modal is visible
 *  - onClose       : () => void
 *  - itemId        : string — inquiry or contact _id
 *  - adminNotes    : array  — existing notes from the record
 *  - type          : "inquiry" | "contact"
 *  - onNotesUpdated: (updatedItem) => void — called after a note is saved so
 *                    the parent can patch its local data without a full refetch
 */
const InquiryNotesModal = ({
  open,
  onClose,
  itemId,
  adminNotes = [],
  type = "inquiry",
  onNotesUpdated,
}) => {
  const message = useMessage();
  const notesListRef = useRef(null);
  const [noteAdminName, setNoteAdminName] = useState("");
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const sortedNotes = [...adminNotes].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  // Scroll to bottom when opened or new note added
  useEffect(() => {
    if (open && notesListRef.current) {
      notesListRef.current.scrollTop = notesListRef.current.scrollHeight;
    }
  }, [open, sortedNotes.length]);

  if (!open) return null;

  const handleSave = async () => {
    if (!noteAdminName.trim()) {
      message.showToast("Please enter admin name", ToastTypes.WARNING);
      return;
    }
    if (!noteText.trim()) {
      message.showToast("Please write a note", ToastTypes.WARNING);
      return;
    }

    setSaving(true);
    try {
      const addFn =
        type === "contact"
          ? InquiryService.addContactNote
          : InquiryService.addNote;

      const res = await addFn(itemId, {
        adminName: noteAdminName.trim(),
        note: noteText.trim(),
      });

      if (res?.status?.code === 200) {
        setNoteText("");
        message.showToast("Note added", ToastTypes.SUCCESS);
        if (onNotesUpdated) onNotesUpdated(res.data);
      } else {
        message.showToast(
          res?.status?.message || "Failed to add note",
          ToastTypes.ERROR
        );
      }
    } catch (error) {
      message.showToast(
        error?.message || "Failed to add note",
        ToastTypes.ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="notes-modal-backdrop"
      onClick={() => !saving && onClose()}
    >
      <div className="notes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notes-modal-header">
          <div>
            <h3>Admin Notes</h3>
            <p className="notes-modal-subtitle">
              Internal notes only visible in admin panel.
            </p>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            disabled={saving}
          >
            ✕
          </button>
        </div>

        <div className="notes-list" ref={notesListRef}>
          {sortedNotes.length ? (
            sortedNotes.map((note, index) => (
              <div
                key={`${note.createdAt}-${index}`}
                className={`note-item ${
                  index === sortedNotes.length - 1 ? "note-latest" : "note-old"
                }`}
              >
                <div className="note-meta">
                  <span className="note-admin">
                    {note.adminName || "Admin"}
                  </span>
                  <span className="note-date">
                    {formatDate(note.createdAt)}
                  </span>
                </div>
                <p className="note-body">{note.note}</p>
              </div>
            ))
          ) : (
            <p className="empty-copy">
              No notes yet. Add a note to help other admins.
            </p>
          )}
        </div>

        <div className="notes-form">
          <div className="form-field">
            <label>Admin Name</label>
            <input
              type="text"
              placeholder="Your Name"
              value={noteAdminName}
              onChange={(e) => setNoteAdminName(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Note</label>
            <textarea
              rows={3}
              placeholder="Write a quick summary or next steps..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
          </div>
        </div>

        <div className="notes-modal-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            disabled={saving}
          >
            Close
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryNotesModal;
